import React, { FC, useState } from "react";
import classNames from "classnames";
import { scoreCls } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlScore } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import { Micro, btnClass } from "../components/ui";
import { DisclosureRow } from "./Disclosure";
import { historyDate } from "./pageModel";

/**
 * "Score history N ▾" (handoff README:181): every ready run, newest first,
 * with its audit and PDF — the modal's PdpScoreHistory table
 * (components/PdpScoreStatus/PdpScoreHistory.tsx:53-99) on the page. The
 * modal keeps its three-line chart; the page's trend is the score block's.
 *
 * A table from sm (columns 1.6fr / .7fr ×3 / 140px); below sm each run is a
 * card on --page with 44px Audit / PDF (09c). The grid carries table roles,
 * and every Audit / PDF button names its run's date, so a screen reader
 * doesn't hear the same "View audit" on every row.
 *
 * The rows come from the block's `usePageScoreHistory`, already newest first.
 * A reload that fails while earlier rows are held keeps them on screen, with
 * a small "Couldn't refresh the score history." line and Try again above
 * them; the full "Couldn't load" error is only for a failure with no rows.
 */

const COLUMNS = { gridTemplateColumns: "minmax(0,1.6fr) repeat(3,minmax(0,.7fr)) 140px" } as const;

const Score: FC<{ value: number | null; size?: number; role?: string }> = ({ value, size = 13, role }) => (
  <span role={role} className={classNames("font-CircularStdBlack tabular-nums", scoreCls(value))} style={{ fontSize: size }}>
    {value == null ? "—" : value}
  </span>
);

/**
 * A run's audit and PDF. `viewLabel` is the visible word — "View audit" in
 * the table, "Audit" on a phone's card; both name the run's `date`.
 */
const RunActions: FC<{ viewLabel: string; date: string; onView: () => void; onPdf: () => void }> = ({
  viewLabel,
  date,
  onView,
  onPdf,
}) => (
  <>
    <button type="button" onClick={onView} className={btnClass("text")} aria-label={`View audit, ${date}`}>
      {viewLabel}
    </button>
    <button type="button" onClick={onPdf} className={btnClass("text")} aria-label={`Download PDF, ${date}`}>
      PDF
    </button>
  </>
);

/**
 * "Couldn't … the score history." and Try again, announced: `--down` when
 * nothing loaded, `--text-2` over held rows. `className` carries the tone
 * and the bottom padding.
 */
const RetryLine: FC<{ text: string; className: string; fontSize: number; onRetry: () => void }> = ({
  text,
  className,
  fontSize,
  onRetry,
}) => (
  <div role="alert" className={classNames("flex flex-wrap items-center gap-x-3 gap-y-1", className)} style={{ fontSize }}>
    {text}
    <button type="button" onClick={onRetry} className={btnClass("text")}>
      Try again
    </button>
  </div>
);

interface Props {
  /** The block's article id — the body's id is derived from it. */
  blockId: string;
  history: PdpUrlScore[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onView: (scoreId: number) => void;
  onPdf: (scoreId: number) => void;
}

type BodyProps = Omit<Props, "blockId">;

/** The open body: skeleton, error, empty line, or the runs (as a table from sm, as cards below). */
const HistoryBody: FC<BodyProps> = ({ history, loading, error, onRetry, onView, onPdf }) => {
  const rows = history || [];
  // A failed reload over rows already held: keep them, with the inline note.
  const staleRows = error && rows.length > 0;

  if (!history && loading) {
    return (
      <div className="flex flex-col gap-2.5 pb-3" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-3 rounded-full bg-track animate-pulse motion-reduce:animate-none" style={{ width: `${80 - i * 12}%` }} />
        ))}
      </div>
    );
  }
  if (error && !staleRows) {
    return <RetryLine text="Couldn't load the score history." className="pb-3 text-down" fontSize={12.5} onRetry={onRetry} />;
  }
  if (rows.length === 0) {
    return (
      // The modal's empty line (PdpScoreHistory.tsx:146).
      <p className="m-0 pb-3 text-text-2" style={{ fontSize: 12.5 }}>
        No completed scores yet — the first one lands a few minutes after the page is added.
      </p>
    );
  }
  // Each run's date, formatted once for both layouts.
  const runs = rows.map((h) => ({ h, date: historyDate(h.scored_at) }));
  // Totals changed meaning in Oct 2026 (summed in code, floors and caps
  // applied after the model): say so when both kinds of run are listed.
  const mixedVersions = rows.some((h) => h.scoring_version === "v1") && rows.some((h) => h.scoring_version && h.scoring_version !== "v1");
  return (
    <>
      {staleRows && (
        <RetryLine text="Couldn't refresh the score history." className="pb-2 text-text-2" fontSize={12} onRetry={onRetry} />
      )}
      {mixedVersions && (
        <p className="m-0 pb-2 text-text-2" style={{ fontSize: 12 }}>
          Runs marked v1 were scored under the earlier method (totals written by the model). Later runs sum the section scores in code, so a step between them is the method, not the page.
        </p>
      )}
      {/* sm and up: the table. */}
      <div className="hidden sm:block pb-2" role="table" aria-label="Score history">
        <div className="grid gap-3 pb-2" style={COLUMNS} role="row">
          <div role="columnheader">
            <Micro>Scored</Micro>
          </div>
          <div role="columnheader">
            <Micro>Overall</Micro>
          </div>
          <div role="columnheader">
            <Micro>Human</Micro>
          </div>
          <div role="columnheader">
            <Micro>Agentic</Micro>
          </div>
          <div role="columnheader">
            <span className="sr-only">Actions</span>
          </div>
        </div>
        {runs.map(({ h, date }) => (
          <div
            key={h.id}
            role="row"
            className="grid gap-3 items-center py-2 border-t border-border-token"
            style={{ ...COLUMNS, fontSize: 13 }}
          >
            <span role="cell" className="text-text-1 min-w-0 truncate">
              {date}
              {mixedVersions && h.scoring_version === "v1" && (
                <span className="ml-1.5 text-text-2" title="Scored under the earlier method">v1</span>
              )}
            </span>
            <Score role="cell" value={h.overall_score} />
            <Score role="cell" value={h.human_conversion_score} />
            <Score role="cell" value={h.agentic_commerce_score} />
            <span role="cell" className="flex justify-end gap-3.5">
              <RunActions viewLabel="View audit" date={date} onView={() => onView(h.id)} onPdf={() => onPdf(h.id)} />
            </span>
          </div>
        ))}
      </div>
      {/* Below sm: one card per run. */}
      <div className="sm:hidden flex flex-col gap-2 pb-2">
        {runs.map(({ h, date }) => (
          <div key={h.id} className="rounded-lg bg-page p-3 flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-text-1" style={{ fontSize: 13 }}>
                {date}
                {mixedVersions && h.scoring_version === "v1" && <span className="ml-1.5 text-text-2">v1</span>}
              </span>
              <Score value={h.overall_score} size={18} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-3 text-text-2" style={{ fontSize: 12 }}>
              <span>
                Human <Score value={h.human_conversion_score} size={12} /> · Agentic{" "}
                <Score value={h.agentic_commerce_score} size={12} />
              </span>
              <span className="flex gap-3">
                <RunActions viewLabel="Audit" date={date} onView={() => onView(h.id)} onPdf={() => onPdf(h.id)} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export const PageScoreHistory: FC<Props> = ({ blockId, history, ...body }) => {
  const [open, setOpen] = useState(false);
  const bodyId = `${blockId}-history`;

  return (
    <div className="flex flex-col">
      <DisclosureRow
        label="Score history"
        count={history ? history.length : null}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        controls={bodyId}
      />
      {/* Built only while open. */}
      <div id={bodyId} hidden={!open}>
        {open && <HistoryBody history={history} {...body} />}
      </div>
    </div>
  );
};
